import React, {startTransition, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {styled} from '@mui/material/styles';
import {
    Typography,
    Box,
    Button,
    Paper,
    Divider,
    TextField,
    IconButton,
    InputAdornment,
    Popper,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem
} from '@mui/material';
import {ArrowBack, ModeEdit, Rule, Search} from '@mui/icons-material';
import Notes from './Notes/Notes';
import EditExperiment from './EditExperiment';
import Popup from '../../components/popup';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AnalysisTable from './AnalysisTable';
import {getAppConfig, useBff, useTrovoConfig} from '../../utils/config';
import {IExperimentDetails} from '../../types/experiment';
import {isDemoExperiment, findDemoAnalysesOfDemoExperiment, findDemoExperimentFromExperimentId} from '../../utils/demo-data';
import Filters from './Filters';

const PageContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column'
});

const ExperimentContent = styled(Box)({
    display: 'flex',
    padding: '0 14%',
    gap: 20,
    background: 'surface-container-lowest',
    alignItems: 'flex-start',
    justifyContent: 'space-between'
});

const TopBar = styled('div')({
    width: '100%',
    padding: '8px 2%',
    display: 'flex',
    alignItems: 'center'
});

const WideCol = styled('div')({
    flex: 4,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'space-between',
    width: '100%'
});
const SmallCol = styled('div')({
    flex: 2,
    display: 'flex',
    flexDirection: 'column',
    //alignItems: "flex-start",
    alignItems: 'space-between',
    gap: 8
});
const ExpFields = styled('div')({
    display: 'flex',
    alignItems: 'flex-start'
});

const ExpField = styled('div')({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
    '& :nth-child(2n)': {
        width: '48%',
        whiteSpace: 'nowrap'
    },
    '& :nth-child(2n+1)': {
        width: '43%',
        whiteSpace: 'nowrap'
    }
});

const BottomContent = styled(Box)({
    padding: '2% 14%'
});

const DESCRIPTION_CLAMP_MIN_CHARS = 600;

interface CreateChipAnalysisButtonProps {
    exp_id: string;
    analysis_count: string;
    navigate: any;
    disabled: boolean;
    hasCompletedDba: boolean;
}

/**
 * Button that opens a menu to create different types of ChIP analyses.
 * Choosing one of them navigates to the correct analysis creation page.
 */
const CreateChipAnalysisButton = ({exp_id, analysis_count, navigate, disabled, hasCompletedDba}: CreateChipAnalysisButtonProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const {featureFlags} = getAppConfig();

    return (
        <>
            <Button
                variant="contained"
                endIcon={<ArrowDropDownIcon />}
                sx={{height: 36, borderRadius: '50px', px: 5}}
                disabled={disabled}
                onClick={e => setAnchorEl(e.currentTarget)}>
                Create Analysis
            </Button>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem
                    onClick={() => {
                        navigate(`/experiment/${exp_id}/analysis/draft/`);
                    }}>
                    <ListItemIcon>
                        <AddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="ChIP default analysis" secondary="Trimming, FastQC, peak calling, and sample annotation" />
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        navigate(`/experiment/${exp_id}/analysis/draft/?mode=functionalAnalysis`);
                    }}
                    disabled={analysis_count === '0'}
                    sx={{flexDirection: 'column', alignItems: 'flex-start'}}>
                    <Box display="flex" alignItems="center" width="100%">
                        <ListItemIcon>
                            <AddIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Functional enrichment analysis" />
                    </Box>
                    {analysis_count === '0' && <Typography variant="caption">*Must create ChIP default analysis first</Typography>}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        navigate(`/experiment/${exp_id}/analysis/draft/?mode=differentialBindingAnalysis`);
                    }}
                    disabled={analysis_count === '0'}
                    sx={{flexDirection: 'column', alignItems: 'flex-start'}}>
                    <Box display="flex" alignItems="center" width="100%">
                        <ListItemIcon>
                            <AddIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Differential binding analysis" />
                    </Box>
                    {analysis_count === '0' && <Typography variant="caption">*Must create ChIP default analysis first</Typography>}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        navigate(`/experiment/${exp_id}/analysis/draft/?mode=motifAnalysis`);
                    }}
                    disabled={analysis_count === '0' || !hasCompletedDba}
                    sx={{flexDirection: 'column', alignItems: 'flex-start'}}>
                    <Box display="flex" alignItems="center" width="100%">
                        <ListItemIcon>
                            <AddIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Motif analysis" />
                    </Box>
                    {analysis_count === '0' ? (
                        <Typography variant="caption">*Must create ChIP default analysis first</Typography>
                    ) : !hasCompletedDba ? (
                        <Typography variant="caption">*Must create differential binding analysis first</Typography>
                    ) : null}
                </MenuItem>
            </Menu>
        </>
    );
};

export function Experiment() {
    let {id} = useParams();
    const navigate = useNavigate();
    const {user} = useTrovoConfig();

    const [analysesDataFromApi] = useBff('GET', `experiment/${id}/analysis`);
    const [experimentDataFromApi, _expError, _expLoading, fetchExperiment] = useBff('GET', `experiment/${id}`);

    const isExperimentDemo = id ? isDemoExperiment(id) : false;
    const isDemo = user.cb_item_price_id?.includes('demo') || isExperimentDemo;

    // pick the source based on demo flag
    const analysesData = isDemo && id ? findDemoAnalysesOfDemoExperiment(id) : analysesDataFromApi;
    const experimentData = isDemo && id ? findDemoExperimentFromExperimentId(id) : experimentDataFromApi;

    const experiment: IExperimentDetails = experimentData;

    // redirect to experiment creation if experiment is still in draft phase
    useEffect(() => {
        if (experiment?.pending && id) {
            navigate(`/experiment/draft/${id}`, {replace: true});
        }
    }, [experiment?.pending, id]);

    const hasCompletedDba =
        Array.isArray(analysesData) &&
        analysesData.some((analysis: any) => analysis?.analysis_type === 'Differential Binding' && analysis?.status === 'completed');
    const [isOpen, setIsOpen] = useState(false);
    const [showFilters, setShowFilters]: any = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredList, setFilteredList]: any = useState(null);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

    const shouldClampDescription = (experiment?.description?.length || 0) > DESCRIPTION_CLAMP_MIN_CHARS;
    const descriptionText = experiment?.description || '';
    const displayedDescription =
        shouldClampDescription && !isDescriptionExpanded
            ? `${descriptionText.slice(0, DESCRIPTION_CLAMP_MIN_CHARS).trimEnd()}...`
            : descriptionText;

    const handleEditExperimentClose = () => {
        setIsOpen(!isOpen);
        fetchExperiment();
    };

    const formatDataForAnalysis = (data: any) => {
        return data?.map((d: any) => {
            return {
                analysis_id: d.id,
                title: d.name || d.analysis_num,
                description: d.description,
                time_started: d.created_time,
                time_completed: d.ex_time,
                status: d.status,
                analysis_num: d.analysis_num,
                experiment_id: d.experiment_id
            };
        });
    };

    const formattedData = formatDataForAnalysis(analysesData);

    const onSearchChange = (e: any) => {
        setSearchTerm(e.target.value);
    };

    // TODO Why is this not a utility function?
    const formatTime = (utcTimeString: string) => {
        const date = new Date(utcTimeString);
        if (isNaN(date.getTime())) {
            return '';
        }

        const userTimeZone = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;
        const formatter =
            new Intl.DateTimeFormat('en-GB', {
                timeZone: userTimeZone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            }) || null;

        const formattedParts = formatter?.formatToParts(date) || null;

        if (formattedParts) {
            const month = formattedParts?.find(part => part.type === 'month')?.value || '??';
            const day = formattedParts?.find(part => part.type === 'day')?.value || '??';
            const year = formattedParts?.find(part => part.type === 'year')?.value || '??';
            const hour = formattedParts?.find(part => part.type === 'hour')?.value || '??';
            const minute = formattedParts?.find(part => part.type === 'minute')?.value || '??';
            const period = formattedParts?.find(part => part.type === 'dayPeriod')?.value.toLowerCase() || '';
            return `${hour}:${minute}${period} ${month}/${day}/${year}`;
        }
        return null;
    };

    useEffect(() => {
        searchTerm
            ? setFilteredList(formattedData?.filter((d: any) => d?.title?.toLowerCase().includes(searchTerm.toLowerCase())))
            : setFilteredList(null);
    }, [searchTerm]);

    useEffect(() => {
        setIsDescriptionExpanded(false);
    }, [id]);

    return (
        <PageContainer>
            <TopBar sx={{bgcolor: 'surface-container-high', mt: '-3px'}}>
                <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    sx={{color: 'on-surface-container', borderRadius: '50px', px: '2.5%'}}
                    startIcon={<ArrowBack />}
                    onClick={() => {
                        startTransition(() => {
                            navigate('/');
                        });
                    }}>
                    Back to Dashboard
                </Button>
            </TopBar>
            <ExperimentContent sx={{bgcolor: 'surface-container-lowest', pt: '2%'}}>
                <WideCol>
                    <Typography
                        variant="headline"
                        size="large"
                        sx={{marginBottom: '2%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px'}}>
                        <span data-testid="name" style={{overflowWrap: 'anywhere', whiteSpace: 'normal'}}>
                            {experiment?.name}
                        </span>
                        <span style={{minWidth: '95px', marginLeft: '8px'}} data-testid="num">
                            EX-{experiment?.experiment_num}
                        </span>
                    </Typography>
                    <Box>
                        <Typography variant="title" size="large" mb={1}>
                            Experiment description
                        </Typography>
                        <Typography variant="body" size="large" mb={1} data-testid="description" sx={{overflowWrap: 'anywhere'}}>
                            {displayedDescription}
                        </Typography>
                        {shouldClampDescription && (
                            <Button
                                variant="outlined"
                                color="primary"
                                sx={{height: '33px', mb: 3}}
                                onClick={() => setIsDescriptionExpanded(prev => !prev)}>
                                <Typography variant="body" size="large">
                                    {isDescriptionExpanded ? 'Show less' : 'See all'}
                                </Typography>
                            </Button>
                        )}
                    </Box>
                </WideCol>
                <SmallCol>
                    <Box bgcolor={'surface-container-low'} borderRadius={4} p={3} my={1}>
                        <Typography variant="title" size="medium" mb={2}>
                            My Samples ({experiment?.sample_count})
                        </Typography>
                        <Typography variant="body" size="medium" mb={1}>
                            View, add, or manage the samples associated with this experiment.
                        </Typography>
                        <Button
                            variant="outlined"
                            disabled={isDemo}
                            fullWidth
                            sx={{my: 2, borderRadius: '50px'}}
                            onClick={() => navigate(`/samples/${id}`)}>
                            Manage Samples
                        </Button>
                    </Box>
                </SmallCol>
            </ExperimentContent>
            <ExperimentContent sx={{bgcolor: 'surface-container-lowest', pb: 2}}>
                <WideCol>
                    <Typography variant="title" size="large" mb={2}>
                        Sample(s) summary
                    </Typography>
                    <ExpFields>
                        <SmallCol sx={{marginRight: '6%'}}>
                            <ExpField>
                                <Typography variant="title" size="medium">
                                    Organism:
                                </Typography>
                                <Typography variant="body" size="large" data-testid="organism">
                                    {experiment?.organism}
                                </Typography>
                            </ExpField>
                            <ExpField>
                                <Typography variant="title" size="medium">
                                    Sequencing Type:
                                </Typography>
                                <Typography variant="body" size="large" data-testid="sequencing_type">
                                    {experiment?.sequencing_type}
                                </Typography>
                            </ExpField>
                            {experiment?.sequencing_type === 'RNA Seq' && (
                                <ExpField>
                                    <Typography variant="title" size="medium">
                                        Analyzed Molecule:
                                    </Typography>
                                    <Typography variant="body" size="large" data-testid="analyzed_molecule">
                                        {experiment?.analyzed_molecule}
                                    </Typography>
                                </ExpField>
                            )}
                            {experiment?.sequencing_type === 'RNA Seq' && (
                                <ExpField>
                                    <Typography variant="title" size="medium">
                                        RNA Selection:
                                    </Typography>
                                    <Typography variant="body" size="large" data-testid="rna_selection_method">
                                        {experiment?.rna_selection_method}
                                    </Typography>
                                </ExpField>
                            )}
                        </SmallCol>
                        <SmallCol sx={{marginLeft: '3%'}}>
                            {experiment?.sequencing_type === 'RNA Seq' && (
                                <ExpField>
                                    <Typography variant="title" size="medium">
                                        Adaptor for Sequencing:
                                    </Typography>
                                    <Typography variant="body" size="large" data-testid="sequencing_adapter">
                                        {experiment?.sequencing_adapter}
                                    </Typography>
                                </ExpField>
                            )}
                            <ExpField>
                                <Typography variant="title" size="medium">
                                    Sequencing Platform:
                                </Typography>
                                <Typography variant="body" size="large" data-testid="sequencing_platform">
                                    {experiment?.sequencing_platform}
                                </Typography>
                            </ExpField>
                            <ExpField>
                                <Typography variant="title" size="medium">
                                    Platform Model:
                                </Typography>
                                <Typography variant="body" size="large" data-testid="platform_model">
                                    {experiment?.platform_model}
                                </Typography>
                            </ExpField>
                        </SmallCol>
                    </ExpFields>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<ModeEdit fontSize="small" />}
                        sx={{alignSelf: 'flex-end', height: '33px', mt: 4}}
                        disabled={isDemo}
                        onClick={() => setIsOpen(true)}>
                        <Typography variant="body" size="large">
                            Edit
                        </Typography>
                    </Button>
                </WideCol>
            </ExperimentContent>
            <BottomContent>
                <Paper
                    sx={{
                        padding: '1.5% 3%',
                        borderRadius: '12px',
                        bgcolor: 'surface-container-lowest',
                        mb: 5,
                        pb: 3,
                        minWidth: '920px'
                    }}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between'}} py={1}>
                        <Typography variant="headline" size="small">
                            My Analyses
                        </Typography>
                        {!id || experiment?.sequencing_type !== 'ChIP Seq' ? (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                disabled={isDemo}
                                sx={{height: '36px', borderRadius: '50px', px: 5}}
                                onClick={() => navigate(`/experiment/${id}/analysis/draft/`)}>
                                Create Analysis
                            </Button>
                        ) : (
                            <CreateChipAnalysisButton
                                exp_id={id}
                                analysis_count={experiment.analysis_count}
                                navigate={navigate}
                                disabled={isDemo}
                                hasCompletedDba={hasCompletedDba}
                            />
                        )}
                    </Box>
                    <Divider />
                    <Box>
                        <TextField
                            label="Search"
                            variant="outlined"
                            size="small"
                            sx={{'& input': {height: '19px'}, my: 1}}
                            onChange={onSearchChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton>
                                            <Search />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <IconButton
                            color="primary"
                            aria-label="filter"
                            onClick={e => setShowFilters(showFilters ? null : e.currentTarget)}
                            sx={{
                                border: '1px solid',
                                borderRadius: '5px',
                                borderColor: 'outline-variant',
                                mx: 1,
                                my: 1,
                                height: '35px',
                                bgcolor: showFilters ? 'surface-container-highest' : ''
                            }}>
                            <Rule />
                            <Typography variant="label" size="large" mx={1}>
                                Filter
                            </Typography>
                        </IconButton>
                        {/* These two buttons were added in May 2024 but never connected to anything. Disabling for now.
                       <span style={{ marginLeft:'53%' }} >
                            <ViewColumn sx={{ fontSize:'25px' }} /> 
                            <Download sx={{ ml:'3px', fontSize:'25px' }} /> 
                        </span>
                        */}
                    </Box>
                    <Popper open={showFilters !== null} anchorEl={showFilters}>
                        <Filters initialList={formattedData} setFilteredList={setFilteredList} />
                    </Popper>

                    <AnalysisTable recentAnalyses={filteredList !== null ? filteredList : formattedData} formatTime={formatTime} />
                </Paper>
                <Notes experimentId={id} formatTime={formatTime} />
            </BottomContent>
            {experiment && (
                <Popup isOpen={isOpen} onClose={handleEditExperimentClose}>
                    <EditExperiment experiment={experiment} onSave={handleEditExperimentClose} />
                </Popup>
            )}
        </PageContainer>
    );
}
